import { useState, useRef, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../api/api';
import { updateUser } from '../redux/actionCreators/user';
import { AUTO_LOGOUT_TIME_MS, CONTINUESLY_API_CALLS } from '../constants';
import { resetFilterTable } from '../redux/actionCreators/tableFilter';
import { updateLastLineItem } from '../redux/actionCreators/station';
import { useMessageContextManager } from '../components/MessageOverlay/MessageOverlay';
import MessageButton from '../components/MessageButton/MessageButton';

const clearStorage = () => {
	// Clear specific localStorage items
	localStorage.removeItem('user');
	localStorage.removeItem('operations');
	localStorage.removeItem('currentLineItem');
	localStorage.removeItem('lastLineItem');
	// Clear any remaining localStorage data
	localStorage.clear();

	// Reset Redux state
	resetFilterTable();
	updateLastLineItem([{}]);
	updateUser({
		token: '',
		plant: { name: '' },
		line: { name: '' },
		station: { name: '' },
	});
};

const loadApiRequestAuthInterceptor = (resetIdleTimer) => {
	let isFirstCallToTargetApi = true;

	const newAuthInterceptor = axiosInstance.interceptors.request.use(
		(config) => {
			if (config.url !== '/auth/signin') {
			}
			const { token } = JSON.parse(localStorage.getItem('user')) || {};
			if (token) {
				config.headers = {
					Authorization: `Bearer ${token}`,
				};
			}

			if (CONTINUESLY_API_CALLS.includes(config.url)) {
				if (isFirstCallToTargetApi) {
					resetIdleTimer();
					isFirstCallToTargetApi = false;
				}
			} else {
				isFirstCallToTargetApi = true;
				resetIdleTimer();
			}
			return config;
		},
		(error) => Promise.reject(error),
	);
	return newAuthInterceptor;
};

const add401ResponseInterceptor = (
	resetIdleTimer,
	logoutUser,
) => {
	let isFirstCallToTargetApi = true;

	axiosInstance.interceptors.response.use(
		(response) => {
			if (CONTINUESLY_API_CALLS.includes(response.url)) {
				if (isFirstCallToTargetApi) {
					resetIdleTimer();
					isFirstCallToTargetApi = false;
				}
			}
			return response;
		},
		async (error) => {
			if (error?.response?.status === 401 || error?.code === 'ERR_NETWORK') {
				logoutUser();
				return new Promise(() => { });
			}
			return Promise.reject(error);
		},
	);
};

const mapDispatchToProps = (dispatch) => ({
	updateUser: (data) => dispatch(updateUser(data)),
});

export const AxiosInterceptorProvider = ({ children, updateUser }) => {
	const navigate = useNavigate();
	const [timeLeft, setTimeLeft] = useState(AUTO_LOGOUT_TIME_MS);
	const idleTimeoutRef = useRef(null);
	const intervalRef = useRef(null);
	const { showMessageOverlay } = useMessageContextManager();

	const logoutUser = () => {
		clearStorage();
		showMessageOverlay({
			error: true,
			contextText: 'Session Expired',
			info: 'Your session has been expired, ReLogin to continue',
			render: ({ close, palette, buttonGradient, themeMode }) => (
				<>
					<MessageButton
						variant="contained"
						label="ReLogin"
						onClick={() => {
							setTimeout(() => {
								if (typeof close === 'function') close();

								// Navigate to login
								navigate('/login');
							}, 700);
						}}
						themeMode={themeMode}
						palette={palette}
						buttonGradient={buttonGradient}
					/>
				</>
			),
		});
	};

	const resetIdleTimer = () => {
		if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
		if (intervalRef.current) clearInterval(intervalRef.current);

		setTimeLeft(AUTO_LOGOUT_TIME_MS);

		idleTimeoutRef.current = setTimeout(() => {
			logoutUser();
		}, AUTO_LOGOUT_TIME_MS);

		intervalRef.current = setInterval(() => {
			setTimeLeft((prevTime) => {
				if (prevTime <= 1000) {
					clearInterval(intervalRef.current);
					return 0;
				}
				return prevTime - 1000;
			});
		}, 1000);
	};

	useLayoutEffect(() => {
		add401ResponseInterceptor(resetIdleTimer, logoutUser);
		loadApiRequestAuthInterceptor(resetIdleTimer);

		// resetIdleTimer();

		return () => {
			if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, []);

	return <>{children}</>;
};

export default connect(null, mapDispatchToProps)(AxiosInterceptorProvider);
